// Reference document for all the events in viewer - https://faro01.atlassian.net/wiki/spaces/AN/pages/3378971889/Sphere+Viewer+Amplitude+Events

export enum EventType {
  // Generic events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3378971889/Sphere+Viewer+Amplitude+Events
  openSheetView = "open sheet view",
  openModel3dView = "open 3dview",
  selectViaProjectOverview = "select via project overview",
  selectLocation = "select location",
  selectCapture = "select capture",
  selectCluster = "select cluster",
  selectScan = "select scan",
  inspectCaptureLocation = "inspect capture location",
  turnSplitscreenOn = "turn splitscreen on",
  turnSplitscreenOff = "turn splitscreen off",
  turnSplitscreenLockOn = "turn splitscreen lock on",
  turnSplitscreenLockOff = "turn splitscreen lock off",
  turnDesaturationOn = "turn desaturation on",
  turnDesaturationOff = "turn desaturation off",
  switchPointCloudStream = "switch point cloud stream",
  takeScreenshot = "take screenshot",
  enableSmartNavigation = "enable smart navigation",
  disableSmartNavigation = "disable smart navigation",
  // ------- PointCloud Management events
  selectPointCloudFile = "select point-cloud file",
  uploadPointCloud = "upload point-cloud",
  cancelUploadViaTaskBar = "cancel upload via task-bar",
  cancelUploadViaProjectTree = "cancel upload via project-tree",
  downloadPointCloud = "download point-cloud",
  downloadImage = "download image",
  alignPointCloud = "align point-cloud",
  deletePointCloud = "delete point-cloud",
  closePageWhileUploading = "close page while uploading",
  closePageWhileBackendProcessing = "close page while backend processing",
  openProgressOverview = "open progress-overview",
  openQuickHelp = "open quick help",
  upload360Photo = "upload 360 photo",
  upload2DPhoto = "upload 2D photo",

  // AI Detection events
  // https://faro01.atlassian.net/wiki/spaces/BUIL/whiteboard/4505174136?atlOrigin=eyJpIjoiNGFlOTM4YThiMTU5NDUyNDgwYmY4Y2RkMGJhOTU2NzIiLCJwIjoiYyJ9&whiteboard-linked-object=667yE0GBykqdo17GZeuo0
  startAiDetection = "start ai detection",
  approveAiDetection = "approve ai detection",
  rejectAiDetection = "reject ai detection",
  openAiDetectionReviewTool = "open ai detection review tool",
  switchAiReviewMode = "switch ai review mode",

  // AI chat events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/6020890736/AI+Chat+Events
  openAiChatPanel = "open AI chat panel",
  closeAiChatPanel = "close AI chat panel",
  sendPromptToAi = "send prompt to AI",
  clearCurrentAiChatHistory = "clear current AI chat history",
  clickOnAiChatLink = "click on AI chat link",
  openAiReasoning = "open AI reasoning",
  closeAiReasoning = "close AI reasoning",
  clickOnAiHelpIcon = "click on AI help icon",
  toggleAiAnnotationsVisibility = "toggle AI annotations visibility",
  hoverOverAiAnnotationTitle = "hover over AI annotation title",

  // Annotation events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3840507995/Annotations
  hoverAnnotationTitle = "hover annotation title",
  expandAnnotationDetails = "expand annotation details",
  closeAnnotationDetails = "close annotation details",
  createAnnotationShareLink = "create annotation share link",
  gotoAnnotation = "goto annotation",
  openAnnotationDetailsContextMenu = "open annotation details context menu",
  editAnnotation = "edit annotation",
  deleteAnnotation = "delete annotation",
  confirmAnnotationDeletion = "confirm annotation deletion",
  cancelAnnotationDeletion = "cancel annotation deletion",
  triggerAnnotationCreationTool = "trigger annotation creation tool",
  createAnnotation = "create annotation",
  cancelAnnotationCreation = "cancel annotation creation",
  cancelAnnotationEditing = "cancel annotation editing",
  saveAnnotation = "save annotation",
  saveExternalAnnotation = "save external annotation",
  openAnnotationList = "open annotation list",
  filterAnnotationList = "filter annotation list",
  sortAnnotationList = "sort annotation list",
  changeAnnotationCardLayout = "change annotation card layout",
  copyAnnotationCoordinate = "copy annotation coordinate",
  addAnnotationAttachments = "add annotation attachment",
  openAnnotationAttachment = "open annotation attachment",
  deleteAnnotationAttachment = "delete annotation attachment",
  confirmDeleteAnnotationAttachment = "confirm annotation attachment deletion",
  cancelDeleteAnnotationAttachment = "cancel annotation attachment deletion",
  authorizeExternalAnnotation = "authorize external annotation",

  // CAD/BIM events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3728769069/CAD+BIM+Amplitude+Events
  selectCadModel = "select cad model",
  selectCadElement = "select cad element",
  alignCad = "align cad",
  downloadCad = "download cad",
  hideCadElement = "hide cad element",
  showCadElement = "show cad element",
  isolateCadElement = "isolate cad element",
  selectCadParentElement = "select cad parent element",
  selectCadChildrenElement = "select cad children element",
  startAlignment = "start alignment workflow",
  confirmAlignment = "confirm alignment",

  // Camera menu settings
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4748476739/Camera+Menu+Events
  enablePerspectiveCamera = "enable perspective camera",
  enableOrthographicCamera = "enable orthographic camera",
  selectCameraPredefinedView = "select camera predefined view",

  // Clipping box events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3987472476/Clipping+Box+Events
  showClippingBox = "show clipping box",
  hideClippingBox = "hide clipping box",
  editClippingBox = "edit clipping box",
  applyAutoClippingBox = "apply auto clipping box",
  resetClippingBox = "reset clipping box",

  // Colored cloud analysis events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4317839382/Colored+cloud+analysis+events
  openColoredAnalysisTool = "open colored analysis tool",
  closeColoredAnalysisTool = "close colored analysis tool",
  closeColoredAnalysisLoop = "close colored analysis loop",
  openColoredAnalysisSubmenu = "open colored analysis submenu",
  hideColoredAnalysis = "hide colored analysis",
  showColoredAnalysis = "show colored analysis",
  saveColoredAnalysis = "save colored analysis",
  deleteColoredAnalysis = "delete colored analysis",
  generateColoredAnalysisDeeplink = "generate colored analysis deeplink",
  selectColoredAnalysisColorScale = "select colored analysis color scale",
  selectColoredAnalysisTolerance = "select colored analysis tolerance",
  selectColoredAnalysisRange = "select colored analysis range",
  toggleVisibilityOfColoredAnalysisReferencePlane = "toggle visibility of colored analysis reference plane",
  selectColoredAnalysisPlaneFitMethod = "select colored analysis plane fit method",
  openColoredAnalysisLabelCreationMode = "open colored analysis label creation mode",
  createColoredAnalysisLabel = "create colored analysis label",
  changeColoredAnalysisUnits = "change colored analysis units",
  createColoredAnalysisReport = "create colored analysis report",

  // Context menu events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5023301804/Context+Menu+Events
  openContextMenu = "open context menu",

  // Control Points Alignment events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4538761257/Control+points+alignment+events
  openControlPointsAlignmentArea = "open control points alignment area",
  exitControlPointsAlignmentArea = "exit control points alignment area",
  confirmControlPointsAlignmentArea = "confirm control points alignment area",
  openControlPointsAlignmentSheet = "open control points alignment sheet",
  exitControlPointsAlignmentSheet = "exit control points alignment sheet",
  confirmControlPointsAlignmentSheet = "confirm control points alignment sheet",

  // Create Area Workflow events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3690037255/Create+Area+Workflow+Events
  openProject = "open project",
  setAreaName = "set area name",
  setFloorImage = "set floor-image",
  changeFloorImage = "change floor-image",

  // Data Organization events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4066967719/Data+Organization+Events
  copyWaypointToAnotherArea = "copy waypoint to another area",
  deleteArea = "delete area",
  deleteSheet = "delete sheet",
  deleteWaypoint = "delete waypoint",
  deleteLocationMarker = "delete location marker",
  deleteTimePoint = "delete time point",
  deleteVideoRecording = "delete video recording",
  editElement = "edit element",
  moveDataSetToSheet = "move data set to sheet",
  moveWaypointToArea = "move waypoint to area",
  moveTimePointToWaypoint = "move time point to waypoint",
  changeWaypointOrder = "change waypoint order",
  changeAreaOrder = "change area order",

  // DeepLink events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3698033334/Deep+links+events
  copyDeepLink = "copy deep-link",
  closeDeepLink = "close deep-link",

  // Manual Blurring events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5695406160/Manual+Blurring+Events
  startManualBlurringTool = "start manual blurring tool",
  drawManualBlur = "draw manual blur",
  undoManualBlur = "undo manual blur",
  redoManualBlur = "redo manual blur",
  discardManualBlurs = "discard manual blurs",
  applyManualBlurs = "apply manual blurs",
  leaveManualBlurringUnintentionally = "leave manual blurring unintentionally",

  // Edit Waypoints Tool events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4861231386/Edit+Waypoints+Tool+Events
  startEditWaypointsTool = "start edit waypoints tool",
  applyEditWaypointsChanges = "apply edit waypoints changes",
  discardEditWaypointsChanges = "discard edit waypoints changes",
  undoEditWaypointsChanges = "undo edit waypoints changes",
  redoEditWaypointsChanges = "redo edit waypoints changes",
  leaveEditWaypointsUnintentionally = "leave edit waypoints unintentionally",
  activateLocationMarkerCreation = "activate location marker creation",
  deactivateLocationMarkerCreation = "deactivate location marker creation",
  placeLocationMarker = "place location marker",
  setLocationMarkerName = "set location marker name",
  editLocationMarkerName = "edit location marker name",
  moveLocationMarker = "move location marker",

  // Export Tool events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4193648670/Export+Tool+Events
  startExportTool = "start export tool",
  exitExportTool = "exit export tool",
  exportPointCloud = "export point-cloud",
  exportAutoClip = "export tool autoclipping",
  exportResetClip = "export tool reset clipping",
  // ----- PointCloud Export (to be removed in https://faro01.atlassian.net/browse/SWEB-5550)
  startPointCloudExport = "start point-cloud export",
  cancelPointCloudExport = "cancel point-cloud export",
  // Orthophoto Export
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4193648670/Export+Tool+Events
  generateOrthophoto = "generate orthophoto",
  cancelOrthophoto = "cancel orthophoto",

  // Exposing internal model alignment coordinate systems (CS) events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4593942529/Exposing+internal+model+alignment+systems
  openModelEditorCS = "open model editor CS",
  cancelEditingModelCS = "cancel editing model CS ",
  modifyModelCS = "modify model CS",
  resetModelPosition = "reset model position",

  // Feature tour events
  // https://faro01.atlassian.net/wiki/x/AoDedgE
  showFeatureTour = "show feature tour",
  changeFeatureTourPage = "change feature tour page",
  skipFeatureTour = "skip feature tour",
  completeFeatureTour = "complete feature tour",

  // Floor plan generation events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4919558648/Floor+Plan+Generation+Events
  generateFloorPlan = "generate floor plan",
  startFloorPlanGenerationFlow = "start floor plan generation flow",
  cancelFloorPlanGenerationFlow = "cancel floor plan generation flow",
  downloadGeneratedFloorPlan = "download generated floor plan",

  // Help center menu events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4491319035/Help+center+menu+events
  openHelpMenu = "open help menu",
  openHelpResource = "open help resource",
  openChat = "open chat",

  // HoloBuilder to Sphere XG migration events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/6451560584/HB-XG+Redirect+Events
  clickOnHbToSxgMigrationLearnMore = "click on HB to SXG migration learn more",
  clickOnSwitchToPreviousUi = "click on switch to previous UI",
  submitHbToSxgMigrationFeedback = "submit HB to SXG migration feedback",
  cancelHbToSxgMigrationFeedback = "cancel HB to SXG migration feedback",

  // Import data events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5373460492/Import+data+events
  openDraftView = "open draft view",
  openImportDataDialog = "open import data dialog",

  // Import area and sheet events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4975951873/Import+Sheet+Amplitude+Events
  startImportAreaWorkflow = "start import area workflow",
  startImportSheetWorkflow = "start import sheet workflow",
  importAreaSelectAll = "import area select all",
  importSheetSelectAll = "import sheet select all",
  importAreaDeselectAll = "import area deselect all",
  importSheetDeselectAll = "import sheet deselect all",
  importAreaSelectPage = "import area select page",
  importSheetSelectPage = "import sheet select page",
  importAreaDeselectPage = "import area deselect page",
  importSheetDeselectPage = "import sheet deselect page",
  importAreaConfirmImport = "import area confirm import",
  importSheetConfirmImport = "import sheet confirm import",
  importAreaTextInputSelection = "import area text input selection",
  importSheetTextInputSelection = "import sheet text input selection",
  importAreaActivateThumbnail = "import area activate thumbnail",
  importSheetActivateThumbnail = "import sheet activate thumbnail",

  // IPX Scan-to-BIM ordering events (prototype, PDI-538)
  startIpxOrder = "start ipx order",
  cancelIpxOrder = "cancel ipx order",
  completeIpxOrder = "complete ipx order",
  ipxRegionOverLimit = "ipx region over limit",
  openIpxOrderStatus = "open ipx order status",

  // Wizard mode events
  viewWizardStep = "view wizard step",

  // Measurement events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3792044249/Measurements
  enableMeasurementMode = "enable measurement mode",
  disableMeasurementMode = "disable measurement mode",
  completeMeasurement = "complete measurement",
  deleteMeasurement = "delete measurement",
  selectMeasurement = "select measurement",
  copyMeasurementToClipboard = "copy measurement to clipboard",
  enableZoomLens = "enable zoom lens",
  disableZoomLens = "disable zoom lens",

  // Multi-sheet management events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4024598618/Multi-Sheet+Events
  toggleMultiSheetMenu = "toggle multi-sheet menu",
  isolateSheet = "isolate sheet",
  activateSheet = "activate sheet",
  deactivateSheet = "deactivate sheet",
  pressMultiSheetItemAlignButton = "press multi-sheet item align button",
  setAreaElevation = "set area elevation",

  // Pairwise Registration events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3836280834/Pairwise+Registration+Workflow+Events
  startPairwiseAutomaticRegistrationTask = "start pairwise automatic registration task",
  cancelPairwiseAutomaticRegistrationTask = "cancel pairwise automatic registration task",
  startPairwiseRegistrationWorkflow = "start pairwise registration workflow",
  abortPairwiseRegistrationWorkflow = "abort pairwise registration workflow",
  finishPairwiseRegistrationWorkflow = "finish pairwise registration workflow",
  showPairwiseRegistrationReport = "show pairwise registration report",
  resetPairwiseRegistrationCamera = "reset pairwise registration camera",
  resetPairwiseRegistrationTransformation = "reset pairwise registration transformation",
  changePairwiseRegistrationView = "change pairwise registration view",

  // PDF Report events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5764645093/PDF+Report+Events
  startPDFReportTool = "start PDF report tool",
  closePDFReportTool = "close PDF report tool",
  generatePDFReport = "generate PDF report",
  selectPDFReportSheet = "select PDF report sheet",

  // Place Pin / Copy coordinate information
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4592639950/Place+Pin+Copy+coordinate+information
  copyCoordinate = "copy coordinate",
  placePin = "place pin",
  deletePin = "delete pin",
  showPinToolbar = "show pin toolbar",
  hidePinToolbarManually = "hide pin toolbar manually",

  // Project search and filter events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5105385492/Project+filter+search+events
  filterProject = "filter project",
  resetProjectFilters = "reset project filters",
  selectDateFilterPreset = "select date filter preset",
  resetDateFilter = "reset date filter",

  // SetScale events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3689742667/Set+Scale+events
  openAreaScaleTool = "open area-scale tool",
  cancelAreaScaleWarning = "cancel area-scale warning",
  confirmAreaScaleWarning = "confirm area-scale warning",
  adjustAreaScaleLine = "adjust area-scale line",
  removeAreaScaleLine = "remove area-scale line",
  editAreaScaleValue = "edit area-scale value",
  setAreaScaleValue = "set area-scale value",
  setAreaScaleUnit = "set area-scale unit",
  applyAreaScale = "apply area-scale",
  discardAreaScale = "discard area-scale",

  // Snapshot events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/5919932446/Snapshot+Events
  startSnapshotTool = "start snapshot tool",
  closeSnapshotTool = "close snapshot tool",
  generateSnapshot = "generate snapshot",
  openSnapshotLink = "open snapshot link",

  // Staging Area Registration Workflow events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4389404673/Staging+Area+Registration+Workflow+Events
  openRegistration = "open registration",
  toggleRegistrationViewSettings = "toggle registration view settings",
  changeScanColors = "change scan colors",
  changeScanMarkersVisibilityMode = "change scan markers visibility mode",
  toggleConnectionLinesVisibility = "toggle connection lines visibility",
  toggleQualityColorCoding = "toggle quality color coding",
  recenterRegistrationView = "recenter registration view",
  changeRegistrationCameraMode = "change registration camera mode",
  changeRegistrationCameraPerspective = "change registration camera perspective",
  changeRegistrationCameraProjection = "change registration camera projection",
  exitRegistrationView = "exit registration view",
  showSimpleRegistrationQualityReport = "show simple registration quality report",
  showDetailedRegistrationQualityReport = "show detailed registration quality report",
  showSimpleConnectionQualityReport = "show simple connection quality report",
  showDetailedConnectionQualityReport = "show detailed connection quality report",
  selectConnectedScans = "select all connected scans to the particular selected scan",
  centerOnSelection = "center the camera on selected scans",
  editRegistration = "edit registration",
  toggleAddConnectionMode = "toggle add connection mode",
  toggleDeleteConnectionMode = "toggle delete connection mode",
  addRegistrationConnection = "add registration connection",
  deleteRegistrationConnection = "delete registration connection",
  changeScanPosition = "change scan position",
  publishRegistration = "publish registration",
  toggleScanVisibility = "toggle scan visibility",
  toggleClusterVisibility = "toggle cluster visibility",
  toggleOverallVisibility = "toggle overall visibility",
  cancelEditing = "cancel editing",
  refineAndPublish = "refine and publish",
  publishWithoutRefinement = "publish without refinement",
  resetRegistrationEdit = "reset registration edit",
  viewInSphereXg = "view in sphere xg",
  clearScanSelection = "clear scan selection",
  deleteScanSelection = "delete scan selection",
  importScans = "import scans",
  importDroneData = "import drone data",
  undoUserEdit = "undo user edit",
  redoUserEdit = "redo user edit",
  resetUserEdits = "reset user edits",
  collapseScanTree = "collapse scan tree",

  // Navigation events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4968775932/Navigation+events
  openProjectOverview = "open project overview",

  // Structured Export
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4812374702/Structured+Export+Events
  openStructuredExportDialog = "open structured export dialog",
  exportStructuredPointCloud = "export structured point cloud",

  // Tag events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3973415147/Tag+Events
  // ------- Filtering
  toggleTagFilterMenu = "toggle tag filter menu",
  toggleTag = "toggle tag",
  clearSelectedTags = "clear selected tags",
  // ------- Management
  openTagManagement = "open tag management",
  createNewTag = "create new tag",
  editTag = "edit tag",
  deleteTag = "delete tag",
  addTag = "add tag",
  removeTag = "remove tag",

  // Tags management mode
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/4644472009/Tags+Management+Mode+events
  openTagsManagementMode = "open tags management mode",
  addTagsToScan = "add tags to scan",
  removeAllTagsFromScan = "remove all tags from scan",
  clearScansSelection = "clear scans selection",
  removeTagFromScan = "remove tag from scan",

  // VideoMode path correction events
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3901129075/VideoMode+Events
  startVideoModeEditor = "start videomode editor",
  selectVideomodeEditorAlignmentTool = "select videomode editor alignment tool",
  selectVideoModeEditorTrajectoryTool = "select videomode editor trajectory tool",
  undoVideoModeEditorChanges = "undo videomode editor changes",
  redoVideoModeEditorChanges = "redo videomode editor changes",
  resetVideoModeEditorChanges = "reset videomode editor changes",
  saveVideoModeEditorChanges = "save videomode editor changes",
  discardVideoModeEditorChanges = "discard videomode editor changes",

  // VideoMode improvements events
  openAreaOverview = "open area overview",
  downloadVideoWalk = "download video walk",

  // Viewing Settings
  // https://faro01.atlassian.net/wiki/spaces/AN/pages/3891822730/Viewing+Settings
  // ------- Visibility Settings Menu
  toggleViewSettingsMenu = "toggle view settings menu",
  toggleWaypointsVisibility = "toggle waypoints visibility",
  toggleWaypointLabelsVisibility = "toggle waypoint labels visibility",
  toggleAnnotationsVisibility = "toggle annotations visibility",
  toggleMeasurementsVisibility = "toggle measurements visibility",
  toggleTrajectoriesVisibility = "toggle trajectories visibility",
  toggleAnalysesVisibility = "toggle analyses visibility",
  changeObjectVisibility = "change object visibility",
  openObjectVisibilityHelp = "open object visibility help",
  selectWaypointsColorization = "select waypoints colorization",
  toggleWaypointHeight = "toggle waypoint height",
  setObjectVisibilityDistance = "set object visibility distance",
  setPointCloudRenderingProfile = "set point cloud rendering profile",
  set3DModelVisibilityDistance = "set 3D model visibility distance",
  selectOverviewOrientation = "select 2D overview orientation",
  openModelVisibilityHelp = "change model visibility",
  // ------- Transparency/opacity sliders
  toggleOpacityPanel = "toggle opacity panel",
  changeMapOpacity = "change map opacity",
  changeCadOpacity = "change cad opacity",
  changePointcloudOpacity = "change pointcloud opacity",

  // Timeseries dropdown
  // ------- https://faro01.atlassian.net/wiki/spaces/AN/pages/3891822730/Viewing+Settings#Timeseries
  toggleTimeseriesDropdownStyle = "toggle timeseries dropdown style",

  // Tag Search
  // ------- https://faro01.atlassian.net/wiki/spaces/AN/pages/6211210351/Tag+Search+Events
  selectTagSuggestion = "select tag suggestion",
}
